import type { PublicSurvey, Submission } from '@survey/core';

const DB_NAME = 'yellowmetrics-pwa';
const DB_VERSION = 1;
const SURVEYS = 'surveys';
const OUTBOX = 'outbox';

export interface OfflineResponse {
  id: string;
  surveyCode: string;
  accountSubject: string | null;
  submission: Submission;
  createdAt: number;
  status: 'pending' | 'failed';
  error?: string;
}

function openDatabase(): Promise<IDBDatabase> {
  if (!('indexedDB' in globalThis)) return Promise.reject(new Error('IndexedDB is unavailable'));
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(SURVEYS)) db.createObjectStore(SURVEYS, { keyPath: 'code' });
      if (!db.objectStoreNames.contains(OUTBOX)) db.createObjectStore(OUTBOX, { keyPath: 'id' });
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('Could not open offline storage'));
  });
}

async function withStore<T>(name: string, mode: IDBTransactionMode, run: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const db = await openDatabase();
  try {
    return await new Promise<T>((resolve, reject) => {
      const tx = db.transaction(name, mode);
      const request = run(tx.objectStore(name));
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error ?? new Error('Offline storage operation failed'));
      tx.onabort = () => reject(tx.error ?? new Error('Offline storage transaction aborted'));
    });
  } finally {
    db.close();
  }
}

export function cacheSurvey(survey: PublicSurvey, accountSubject: string | null): Promise<void> {
  return withStore(SURVEYS, 'readwrite', (store) => store.put({ ...survey, code: survey.code.toUpperCase(), accountSubject })).then(() => undefined);
}

export async function getCachedSurvey(code: string, accountSubject: string | null): Promise<PublicSurvey | undefined> {
  const cached = await withStore<(PublicSurvey & { accountSubject?: string | null }) | undefined>(SURVEYS, 'readonly', (store) => store.get(code.toUpperCase()));
  if (cached?.requireAuth && (!accountSubject || cached.accountSubject !== accountSubject)) return undefined;
  return cached;
}

export async function queueResponse(surveyCode: string, submission: Submission, accountSubject: string | null): Promise<void> {
  const item: OfflineResponse = {
    id: `${surveyCode.toUpperCase()}:${submission.clientSubmissionId}`,
    surveyCode: surveyCode.toUpperCase(),
    accountSubject,
    submission,
    createdAt: Date.now(),
    status: 'pending',
  };
  await withStore(OUTBOX, 'readwrite', (store) => store.put(item));
}

export function listQueuedResponses(): Promise<OfflineResponse[]> {
  return withStore<OfflineResponse[]>(OUTBOX, 'readonly', (store) => store.getAll());
}

export function deleteQueuedResponse(id: string): Promise<void> {
  return withStore(OUTBOX, 'readwrite', (store) => store.delete(id)).then(() => undefined);
}

export function markQueueFailure(id: string, error: string): Promise<void> {
  return updateQueueItem(id, (item) => ({ ...item, status: 'failed', error }));
}

export function markQueuePending(id: string): Promise<void> {
  return updateQueueItem(id, (item) => ({ ...item, status: 'pending', error: undefined }));
}

function updateQueueItem(id: string, update: (item: OfflineResponse) => OfflineResponse): Promise<void> {
  return openDatabase().then((db) => new Promise<void>((resolve, reject) => {
    const tx = db.transaction(OUTBOX, 'readwrite');
    const store = tx.objectStore(OUTBOX);
    const request = store.get(id);
    request.onsuccess = () => {
      const item = request.result as OfflineResponse | undefined;
      if (item) store.put(update(item));
    };
    request.onerror = () => reject(request.error ?? new Error('Could not load queued response'));
    tx.onerror = () => reject(tx.error ?? new Error('Could not update queued response'));
    tx.onabort = () => reject(tx.error ?? new Error('Offline storage transaction aborted'));
    tx.oncomplete = () => resolve();
  }).finally(() => db.close()));
}
